import * as _ from 'lodash-es';
import { useAllGroupMap } from '../../groups/useAllGroups';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import {
  groupToEntity,
  postToEntity,
  topicToEntity,
} from '../../search/entitySearch/localEntitySearch';
import { useAllTopicMap } from '../../topic/useAllTopics';
import type { ProvisionEntitySearchConfigValues } from './useProvisionEntitySearchConfig';
import useProvisionEntitySearchConfig from './useProvisionEntitySearchConfig';

const sortByName = <T extends { name: string }>(items: T[]) =>
  items
    .flat()
    .toSorted((l, r) =>
      l.name.toUpperCase().localeCompare(r.name.toUpperCase()),
    );

const useProvisionEntities = (values: ProvisionEntitySearchConfigValues) => {
  const { config } = useProvisionEntitySearchConfig(values);
  const allPostMap = useAllSlimPostMap();
  const allTopicMap = useAllTopicMap();
  const allGroupMap = useAllGroupMap();

  const toPostEntities = (ids: string[] = []) =>
    _.compact(
      ids.map((id) => {
        const post = allPostMap[id];
        return post && postToEntity(post);
      }),
    );

  const favoritePostEntities = toPostEntities(values.favoriteIds);
  const membershipPostEntities = toPostEntities(values.memberIds);

  const toTopicEntities = (ids: string[] = []) =>
    _.compact(
      ids.map((id) => {
        const topic = allTopicMap[id];
        return topic && topicToEntity(topic, { extra: config.topic?.extra });
      }),
    );

  const favoriteTopicEntities = toTopicEntities(values.favoriteIds);
  const membershipTopicEntities = toTopicEntities(values.memberIds);

  const membershipGroupEntities = _.compact(
    values.memberIds.map((id) => {
      const group = allGroupMap[id];
      return group && groupToEntity(group);
    }),
  );

  const favorites = sortByName(
    [favoritePostEntities, favoriteTopicEntities].flat(),
  );

  const memberships = sortByName(
    [
      membershipTopicEntities,
      membershipGroupEntities,
      membershipPostEntities,
    ].flat(),
  );

  return { favorites, memberships };
};

export default useProvisionEntities;
